/**
 * Página /admin/declarations — cola global de declaraciones.
 *
 * Para qué: cuando el contador maneja varios tenants, ver el panorama
 * completo (no entrando 1 a 1 a cada tenant-detail) acelera el trabajo
 * de cierre del mes. Acá puede:
 *   - Ver todas las declaraciones ordenadas por vencimiento ASC.
 *   - Filtrar por status (draft / submitted / paid / pending_review).
 *   - Descargar el Hechauka inline para presentar a SET.
 *   - Saltar al detalle del tenant.
 *
 * Reusa el endpoint `GET /api/admin/declarations` que ya devuelve los
 * últimos 100 ordenados por dueDate desc. Para una cola "del mes", el
 * contador filtra por status='draft' y ve qué le falta cerrar.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Download, FileSpreadsheet, Filter } from 'lucide-react';
import { api } from '@/lib/api';
import { AdminShell } from '@/components/admin-shell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { downloadHechauka } from '@/lib/downloads';

interface DeclarationRow {
  id: string;
  tenantId: string;
  kind: string;
  formCode: string;
  periodYear: number;
  periodMonth: number | null;
  dueDate: string;
  status: string;
  netToPay: string;
}

interface DeclarationsResponse {
  items: DeclarationRow[];
}

type StatusFilter = 'all' | 'draft' | 'pending_review' | 'submitted' | 'paid' | 'rectified';

export function DeclarationsListPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-declarations'],
    queryFn: () => api<DeclarationsResponse>('/api/admin/declarations'),
  });

  // Ordenar por dueDate ASC (las que vencen primero arriba). El endpoint
  // devuelve DESC por defecto — invertimos en cliente para esta vista.
  const sorted = useMemo(() => {
    const items = data?.items ?? [];
    return [...items].sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  }, [data]);

  const filtered = useMemo(
    () => (statusFilter === 'all' ? sorted : sorted.filter((d) => d.status === statusFilter)),
    [sorted, statusFilter]
  );

  // Contadores por status — útil para el header "5 en draft, 3 submitted…"
  const counts = useMemo(() => {
    const c: Record<string, number> = {
      all: sorted.length,
      draft: 0,
      pending_review: 0,
      submitted: 0,
      paid: 0,
      rectified: 0,
    };
    for (const d of sorted) {
      if (d.status in c) c[d.status] = (c[d.status] ?? 0) + 1;
    }
    return c;
  }, [sorted]);

  return (
    <AdminShell>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <FileSpreadsheet />
            Declaraciones
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Cola global de cierres fiscales. Ordenado por vencimiento.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="size-4 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas ({counts.all})</SelectItem>
              <SelectItem value="draft">Borrador ({counts.draft})</SelectItem>
              <SelectItem value="pending_review">Pendiente revisión ({counts.pending_review})</SelectItem>
              <SelectItem value="submitted">Presentadas ({counts.submitted})</SelectItem>
              <SelectItem value="paid">Pagadas ({counts.paid})</SelectItem>
              <SelectItem value="rectified">Rectificadas ({counts.rectified})</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading && <p className="text-muted-foreground">Cargando…</p>}

      {!isLoading && filtered.length === 0 && (
        <div className="border border-dashed border-border rounded-lg py-12 text-center text-muted-foreground">
          {statusFilter === 'all'
            ? 'No hay declaraciones generadas. Andá a un contacto y generá la primera IVA del mes.'
            : `No hay declaraciones en estado "${statusFilter}".`}
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Contacto</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Formulario</TableHead>
              <TableHead>Período</TableHead>
              <TableHead>Vence</TableHead>
              <TableHead className="text-right">A pagar</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((d) => (
              <TableRow key={d.id}>
                <TableCell>
                  <Link
                    href={`/admin/tenants/${d.tenantId}?tab=declarations`}
                    className="text-purple hover:underline"
                  >
                    Ver contacto
                  </Link>
                </TableCell>
                <TableCell>{d.kind.toUpperCase()}</TableCell>
                <TableCell className="font-mono text-xs">{d.formCode}</TableCell>
                <TableCell>
                  {d.periodMonth ? `${String(d.periodMonth).padStart(2, '0')}/${d.periodYear}` : d.periodYear}
                </TableCell>
                <TableCell>{new Date(d.dueDate + 'T12:00:00').toLocaleDateString('es-PY')}</TableCell>
                <TableCell className="text-right font-mono">
                  Gs {Number(d.netToPay).toLocaleString('es-PY')}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={
                      d.status === 'submitted' || d.status === 'paid'
                        ? 'success'
                        : d.status === 'draft'
                        ? 'secondary'
                        : 'warning'
                    }
                  >
                    {d.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {d.formCode === 'F.120' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadHechauka(d.id)}
                      title="Descargar TXT para subir a Hechauka/Marangatu"
                    >
                      <Download />
                      Hechauka
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </AdminShell>
  );
}

